import React, { useRef, useState, useEffect, useCallback } from "react";

import styles from "styles.module.css";
import Filter from "./Filter";
import "./styles.css";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { addMonths } from "date-fns";
import OverLayLoading from "ui/Loading";
import GridCustomPage from "ui/grid/GridPage";
import { useTranslation } from "react-i18next";
import { GridCellProps } from "@progress/kendo-react-grid";
import { PageTypePath } from "utils/pages";
import { COLOR, ICON_NAMES } from "utils/constant";
import {
	confirmSalesConfirmationApi,
	getSalesConfirmationApi,
	getSalesConfirmationLinesApi,
	SalesConfirmationFilter,
} from "api/salesConfirmation";
import { SalesConfirmation } from "interface/sales";
import {
	salesConfirmationDetailGridItems,
	SalesConfirmationGridColumn,
} from "./GridColumns";
import CustomDetailedPage from "ui/grid/DetailPage";
import SalesConfirmationMobileList from "./SalesConfirmationMobileList";
import CustomModal from "ui/modal/Modal";

import { Loading } from "ui/Loading";
import { IconButtonActionType } from "interface";
import { usePageAccess } from "hooks/usePageAccess";
import { useUserStore } from "store";
import { useInfoStore } from "store/error";

const pageSize = 60;

const SalesConfirmations = () => {
	const { t } = useTranslation();

	const overlayRef = useRef<HTMLDivElement>(null);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);

	const gridRef = useRef<HTMLDivElement>(null);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1600);
	const [mobileData, setMobileData] = useState<SalesConfirmation[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);

	const [filterOptions, setFilterOptions] = useState<SalesConfirmationFilter>({
		take: pageSize,
		skip: 0,
		fromDate: addMonths(new Date(), -1),
		toDate: new Date(),
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const [saleConfirmationUpdate, setSaleConfirmationUpdate] = useState(false);

	const [pendingConfirmProp, setPendingConfirmProp] = useState<GridCellProps | null>(null);
	const [pendingConfirmItem, setPendingConfirmItem] = useState<SalesConfirmation | null>(null);
	const pendingResolveRef = useRef<(() => void) | null>(null);
	const pendingRejectRef = useRef<((err: unknown) => void) | null>(null);

	const { pageAccess } = usePageAccess();
	const { isAdmin } = useUserStore();
	const { setError } = useInfoStore();

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const openPdf = (prop: GridCellProps) => {
		window.open(`/${PageTypePath.Pdf}/${prop.dataItem.reportGuid}`, "_blank");
	};

	const openPdfByItem = (item: SalesConfirmation) => {
		window.open(`/${PageTypePath.Pdf}/${item.reportGuid}`, "_blank");
	};

	const confirmSales = (prop: GridCellProps) => {
		setPendingConfirmProp(prop);
	};

	const confirmSalesByItem = (item: SalesConfirmation): Promise<void> => {
		return new Promise<void>((resolve, reject) => {
			setPendingConfirmItem(item);
			pendingResolveRef.current = resolve;
			pendingRejectRef.current = reject;
		});
	};

	const handleConfirmDialogOk = () => {
		if (pendingConfirmProp) {
			confirmSalesConfirmationApi({
				id: pendingConfirmProp.dataItem.id,
				confirmationTypeId: pendingConfirmProp.dataItem.salesConfirmationTypeId,
			})
				.then(() => {
					setSaleConfirmationUpdate((prev) => !prev);
				})
				.catch((err) => {
					if (err?.response?.data) {
						setError(err?.response?.data.returnText);
					} else {
						setError(err.message);
					}
				})
				.finally(() => {
					setPendingConfirmProp(null);
				});
		} else if (pendingConfirmItem) {
			confirmSalesConfirmationApi({
				id: pendingConfirmItem.id,
				confirmationTypeId: pendingConfirmItem.salesConfirmationTypeId,
			})
				.then(() => {
					setSaleConfirmationUpdate((prev) => !prev);
					pendingResolveRef.current?.();
				})
				.catch((err) => {
					if (err?.response?.data) {
						setError(err?.response?.data.returnText);
					} else {
						setError(err.message);
					}
					pendingRejectRef.current?.(err);
				})
				.finally(() => {
					setPendingConfirmItem(null);
					pendingResolveRef.current = null;
					pendingRejectRef.current = null;
				});
		}
	};

	const handleConfirmDialogCancel = () => {
		setPendingConfirmProp(null);
		setPendingConfirmItem(null);
		pendingResolveRef.current?.();
		pendingResolveRef.current = null;
		pendingRejectRef.current = null;
	};

	const requestMobileData = useCallback((skip: number, take: number, resetData = false) => {
		if (mobileRequestInProgress.current || mobileDataFinished.current) {
			return;
		}

		mobileRequestInProgress.current = true;
		setMobileLoading(true);

		getSalesConfirmationApi({
			...filterOptions,
			skip,
			take,
		})
			.then((response) => {
				const incoming = (response?.data || []) as SalesConfirmation[];

				if (incoming.length === 0) {
					mobileDataFinished.current = true;
					if (resetData) {
						setMobileData([]);
					}
					return;
				}

				mobileNextSkip.current = skip + take;

				if (resetData) {
					setMobileData(incoming);
				} else {
					setMobileData((prev) => [...prev, ...incoming]);
				}

				const loadedCount = skip + incoming.length;
				if (loadedCount >= (response?.totalCount || 0)) {
					mobileDataFinished.current = true;
				}
			})
			.finally(() => {
				mobileRequestInProgress.current = false;
				setMobileLoading(false);
			});
	}, [filterOptions]);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 1600);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, requestMobileData, saleConfirmationUpdate]);

	if (!pageAccess) {
		return <Loading show={true} />;
	}

	const showConfirmDialog = pendingConfirmProp !== null || pendingConfirmItem !== null;

	return (
		<div className={`${styles.PageContainer} sales-confirmation-page`}>
			<div ref={overlayRef} style={{ display: "none" }}>
				<OverLayLoading />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("SalesConfirmations")}
				</legend>
			</div>

			{showConfirmDialog && (
				<CustomModal
					title={t("Confirm")}
					onClose={handleConfirmDialogCancel}
					actions={[
						{
							label: t("Cancel"),
							onClick: handleConfirmDialogCancel,
							bgColor: COLOR.Green,
							color: "white",
						},
						{
							label: t("Ok"),
							onClick: handleConfirmDialogOk,
							bgColor: COLOR.Green,
							color: "white",
						},
					]}
				>
					<p style={{ padding: "16px 0" }}>
						{t("SalesConfirmationConfirm")}
					</p>
				</CustomModal>
			)}

			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{isMobileView ? (
					<SalesConfirmationMobileList
						items={mobileData}
						loading={mobileLoading}
						onConfirm={confirmSalesByItem}
						onOpenPdf={openPdfByItem}
						onLoadMore={() => requestMobileData(mobileNextSkip.current, pageSize)}
					/>
				) : (
					<GridCustomPage
						dataKey="id"
						gridColumns={SalesConfirmationGridColumn(isAdmin)}
						detailPage={(prop) => (
							<CustomDetailedPage
								grids={salesConfirmationDetailGridItems}
								dataApi={() =>
									getSalesConfirmationLinesApi({
										salesId: prop.dataItem.id,
										confirmationTypeId: prop.dataItem.salesConfirmationTypeId,
									})
								}
							/>
						)}
						filter={filterOptions}
						exportGridAsPDFRef={pdfRef}
						lastColWidth="140px"
						forceUpdate={saleConfirmationUpdate}
						lastColBtns={[
							{
								iconName: ICON_NAMES.Confirmation,
								showConditionValue: 1,
								conditionField: "status",
								onclick: confirmSales,
								actionType: IconButtonActionType.Update,
							},
							{
								iconName: ICON_NAMES.Confirmation,
								showConditionValue: 2,
								conditionField: "status",
								onclick: confirmSales,
								actionType: IconButtonActionType.Update,
							},
							{
								iconName: ICON_NAMES.Download,
								alwaysShow: true,
								onclick: openPdf,
							},
						]}
						pageAccess={pageAccess}
						exportGridAsExcelRef={excelRef}
						apiMethod={getSalesConfirmationApi}
					/>
				)}
			</div>
		</div>
	);
};

export default SalesConfirmations;
